/**
 * PvpCountersCard — "what beats X in <league>" with the counters first (plan phase 5, decision 0021).
 *
 * The backend's ``pvp_counters_card`` carries one ``counters`` section per
 * league: PvPoke's counters with their battle ratings, worst matchup first,
 * then the target's own rank, verdict and best moves as context. Rendered by
 * CardRenderer; without stored counters the backend sends the ranking card.
 */
import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";
import PokemonSprite from "../PokemonSprite";

interface Props { payload: VisualPayload }
interface Counter { name: string; rating?: number | null }

const VERDICT_STYLE: Record<string, string> = {
  "Strong pick":     "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  "Usable":          "bg-sky-500/20 text-sky-300 border-sky-500/30",
  "Not recommended": "bg-white/10 text-white/60 border-white/15",
};

function LeagueBlock({ sec }: { sec: Record<string, unknown> }) {
  const counters = Array.isArray(sec.counters) ? (sec.counters as Counter[]) : [];
  const league = typeof sec.league === "string" ? sec.league : "";
  const moves = [sec.best_fast_move, sec.best_charge_move_1, sec.best_charge_move_2].filter((m): m is string => typeof m === "string" && m.length > 0);
  return (
    <div className="rounded-xl border border-white/[0.07] p-3 space-y-3">
      {league && <p className="text-xs font-semibold capitalize text-white/70">{league} League</p>}
      <ol className="space-y-1">
        {counters.map((c, i) => (
          <li key={c.name} className="flex items-center justify-between text-sm">
            <span className="text-white/90"><span className="text-white/30 mr-2 tabular-nums">{i + 1}.</span>{c.name}</span>
            {typeof c.rating === "number" && <span className="text-[11px] font-mono text-white/40">{c.rating}</span>}
          </li>
        ))}
      </ol>
      {sec.rank != null && (
        <div className="bg-white/[0.03] rounded-lg p-2.5 border border-white/[0.05] space-y-1">
          <div className="flex items-center gap-2">
            <p className="text-[10px] text-white/30 uppercase tracking-wide">{String(sec.target ?? "Target")} itself</p>
            <span className="text-xs text-scout-yellow font-semibold">#{String(sec.rank)}</span>
            {typeof sec.verdict === "string" && sec.verdict in VERDICT_STYLE && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border leading-none ${VERDICT_STYLE[sec.verdict]}`}>{sec.verdict}</span>
            )}
          </div>
          {moves.length > 0 && <p className="text-xs text-white/60">Moves you will face: {moves.join(" · ")}</p>}
        </div>
      )}
    </div>
  );
}

export default function PvpCountersCard({ payload }: Props) {
  const sections = payload.sections.filter((s) => s.section === "counters") as Record<string, unknown>[];
  const first = sections[0];
  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      {sections.length === 0 ? (
        <p className="text-sm text-white/40 py-4 text-center">No PvP counter data yet.</p>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <PokemonSprite pokedexId={first.pokedex_id as number | undefined} alt={String(first.target ?? "")} size="lg" />
            <p className="text-[10px] text-white/30 uppercase tracking-wide">Beaten most by · PvPoke battle rating</p>
          </div>
          {sections.map((sec) => <LeagueBlock key={String(sec.league)} sec={sec} />)}
        </div>
      )}
    </CardShell>
  );
}
